import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import moment from 'moment';
import { makeRESTApiRequest } from '../../helpers/api';
import { ENDPOINTS } from '../../helpers/constants';
import DataTable from '../DataTable';
import { SquareArrowOutUpRight, Building2, Phone, Mail, Globe } from 'lucide-react';
import { Container, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { getUserFromLocalstrorage, isClient } from '../../helpers/global';
import { formatPhoneNumber } from '../hoaForm/FormattedPhoneField';

const formatPhoneDisplay = value => {
  if (!value) return '';
  const formatted = formatPhoneNumber(value);
  return formatted || String(value);
};

const getHeaders = user => {
  const userRole = user?.role;
  const isOrgRole = isClient(userRole);

  const columns = [
    {
      name: 'Address',
      minWidth: 250,
      render: row =>
        isOrgRole ? (
          row['Address']
        ) : (
          <a
            href={row['Service Link']}
            target="_blank"
            rel="noopener noreferrer"
            className="text-info"
            style={{ textDecoration: 'underline' }}
          >
            {row['Address']} <SquareArrowOutUpRight className="icon-sm ml-1" />
          </a>
        )
    },
    {
      name: 'HOA1 Details',
      minWidth: 350,
      render: row => renderHoaDetailsCell(row['HOA1 Details'])
    },
    {
      name: 'HOA2 Details',
      minWidth: 350,
      render: row => renderHoaDetailsCell(row['HOA2 Details'])
    },
    {
      name: 'HOA3 Details',
      minWidth: 350,
      render: row => renderHoaDetailsCell(row['HOA3 Details'])
    },
    {
      name: 'Date Delivered',
      minWidth: 200,
      render: row => {
        const dateDelivered = row['Date Delivered'];
        if (!dateDelivered) return 'N/A';
        return moment(dateDelivered).format('MMM YYYY');
      }
    },
    { name: 'FOLIO NO #' },
    {
      name: 'Legal Description',
      minWidth: 250,
      render: row => {
        const hoaData = [row['HOA1 Details'], row['HOA2 Details'], row['HOA3 Details']];
        const maxFields = hoaData.reduce((max, hoa) => {
          if (!hoa) return max;
          const fieldCount = [hoa.name, hoa.organization, hoa.phone, hoa.email].filter(field => field && field.trim())
            .length;
          return Math.max(max, fieldCount);
        }, 0);

        const estimatedLines = Math.min(Math.max(maxFields, 2), 5);

        return (
          <OverlayTrigger
            key={`legal-description-${row._id}`}
            placement="bottom"
            delay={50}
            overlay={
              <Tooltip className="custom-tooltip" id={`legal-description-${row._id}`} style={{ minWidth: 400 }}>
                <>{row['Legal Description'] || '-'}</>
              </Tooltip>
            }
          >
            <div
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                lineHeight: '1.4',
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: estimatedLines,
                cursor: 'pointer',
                wordBreak: 'break-word'
              }}
            >
              {row['Legal Description'] || '-'}
            </div>
          </OverlayTrigger>
        );
      }
    }
  ];

  return columns;
};

const renderHoaDetailsCell = hoa => {
  if (!hoa) return 'N/A';

  const { name, organization, platform, phone, email, noEstoppelNeeded } = hoa;

  const rows = [
    name && (
      <div key="name" className="d-flex align-items-center mb-1 ">
        <span className="font-weight-semibold">{name}</span>
      </div>
    ),
    noEstoppelNeeded && (
      <div key="no-estoppel" className="text-danger xsmallFont font-weight-bold mb-1">
        No estoppel needed
      </div>
    ),
    organization && (
      <div key="org" className="d-flex align-items-center mb-1">
        <Building2 size={14} className="mr-1 text-muted flex-shrink-0" />
        <span className="text-muted xsmallFont">{organization}</span>
      </div>
    ),
    platform && (
      <div key="platform" className="d-flex align-items-center mb-1">
        <Globe size={14} className="mr-1 text-muted flex-shrink-0" />
        <span className="text-muted xsmallFont">{platform}</span>
      </div>
    ),
    phone && (
      <div key="phone" className="d-flex align-items-center mb-1">
        <Phone size={14} className="mr-1 text-muted flex-shrink-0" />
        <a
          className="text-muted xsmallFont"
          href={`tel:${String(phone).replace(/\D/g, '')}`}
          onClick={e => e.stopPropagation()}
        >
          {formatPhoneDisplay(phone)}
        </a>
      </div>
    ),
    email && (
      <div key="email" className="d-flex align-items-center mb-1">
        <Mail size={14} className="mr-1 text-muted flex-shrink-0" />
        <a className="text-info xsmallFont" href={`mailto:${email}`} onClick={e => e.stopPropagation()}>
          {email}
        </a>
      </div>
    )
  ].filter(Boolean);

  return rows.length ? <div className="d-flex flex-column">{rows}</div> : 'N/A';
};

const HoaExplorer = () => {
  const [loading, setLoading] = useState(false);
  const [hoas, setHoas] = useState([]);
  const [toLoadPageInfo, setToLoadPageInfo] = useState({
    pageNumber: 1
  });
  const [loadedPageInfo, setLoadedPageInfo] = useState({
    totalPages: 1,
    resultCount: 0,
    pageNumber: 1
  });
  const [searchQuery, setSearchQuery] = useState('');

  const currentUser = getUserFromLocalstrorage();
  // Compute headers based on role
  const headers = useMemo(() => getHeaders(currentUser), [currentUser]);

  const fetchHoas = async () => {
    setLoading(true);

    const { response, error } = await makeRESTApiRequest({
      endpoint: ENDPOINTS.FETCH_ORDERS_HOAS,
      requestBody: { page: toLoadPageInfo.pageNumber, query: searchQuery }
    });

    if (error) {
      toast.error(error);
      setLoading(false);
      return;
    }

    const rows = (response?.results || []).filter(s => s.service !== null);
    setHoas(rows);
    setLoadedPageInfo({
      ...loadedPageInfo,
      totalPages: response?.totalPages || 1,
      pageNumber: response?.page || toLoadPageInfo.pageNumber
    });
    setLoading(false);
  };

  const extractHoaDetails = hoa => {
    if (!hoa) return { name: '', organization: '', platform: '', phone: '', email: '' };

    // After enrichment, data is already in name/managementCompany/phoneNumber/email fields
    const name = hoa.name || '';
    const organization = hoa.managementCompany || '';
    const platform = hoa.orderingPlatform || '';
    let phone = hoa.phoneNumber || '';
    let email = hoa.email || '';

    // Fallback to first linked contact when main fields are empty
    const fallbackContacts = hoa.managementCompany
      ? hoa.managementCompanyContacts
      : hoa.associationContacts;
    if (!phone && Array.isArray(fallbackContacts) && fallbackContacts.length > 0) {
      phone = fallbackContacts[0]?.directPhone || '';
    }
    if (!email && Array.isArray(fallbackContacts) && fallbackContacts.length > 0) {
      email = fallbackContacts[0]?.directEmail || '';
    }

    return { name, organization, platform, phone, email, noEstoppelNeeded: !isClient(currentUser?.role) && Boolean(hoa.noEstoppelNeeded) };
  };

  const tableData = useMemo(() => {
    if (hoas) {
      const data = [];
      [...hoas].forEach(h => {
        let obj = {};
        obj['_id'] = h['_id'];
        obj['cleanedFolioNumbers'] = h.service['cleanedFolioNumbers'];
        obj['Address'] = h.service['propertyAddress'];
        obj['FOLIO NO #'] = h.service['folioNumbers'];
        obj['Service Link'] = `${process.env.REACT_APP_URL}/services/${h.service._id}`;
        [...h.homeOwnerInformation].forEach((hoa, index) => {
          obj[`HOA${index + 1} Details`] = extractHoaDetails(hoa);
        });
        obj['Date Delivered'] = h?.dateDelivered || null;
        obj['Legal Description'] = h.service['legalDescription'];
        data.push(obj);
      });
      const uniqueArray = data.filter((value, index) => {
        const _value = JSON.stringify(value);
        return (
          index ===
          data.findIndex(obj => {
            return JSON.stringify(obj) === _value;
          })
        );
      });
      return uniqueArray;
    }
  }, [hoas]);

  const handlePageChange = pageNumber => {
    setToLoadPageInfo({ ...toLoadPageInfo, pageNumber });
  };

  const handleSearch = query => {
    setSearchQuery(query);
  };

  useEffect(() => {
    setToLoadPageInfo({ ...toLoadPageInfo, pageNumber: 1 });
  }, [searchQuery]);

  useEffect(() => {
    fetchHoas();
  }, [toLoadPageInfo]);

  return (
    <Container fluid className="d-flex flex-column" style={{ height: 'calc(100vh - 80px)', overflow: 'hidden' }}>
      <h6 className="font-weight-bold xxxlargeFont my-3">HOA Explorer</h6>
      <DataTable
        searchHint="Search..."
        headers={headers}
        data={tableData}
        rowKeyField="_id"
        searchQueryKeys={[]}
        useInternalSearch={false}
        onSearch={handleSearch}
        pagination={loadedPageInfo}
        onPageChange={handlePageChange}
        loadingData={loading}
      />
    </Container>
  );
};

export default HoaExplorer;
